import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Briefcase, Calendar, CheckCircle, XCircle, Star, Eye,
  Clock, MapPin, Download, Search, Mail, X, Phone, ExternalLink,
  Send, Upload, DollarSign, GraduationCap, AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { ApplicationStatus, EmploymentType, JobInvitation } from '../types'
import { SALARY_RANGE_OPTIONS, toDbValue, toDisplayValue } from '../constants/profileOptions'
import { notifyNewApplicationEmail } from '../utils/emailNotify'

interface MyApplication {
  id: string
  job_id: string
  status: ApplicationStatus
  applied_at: string
  reviewed_at?: string
  cover_letter?: string
  resume_url?: string
  employer_email?: string
  application_data?: Record<string, any>
  job_title: string
  job_employment_type?: EmploymentType
  job_location?: string
  company_name: string
  job_description?: string
  job_requirements?: string[]
  job_skills_required?: string[]
  job_salary_range?: string
  job_deadline?: string
  job_experience_level?: string
  job_is_partner_listing?: boolean
  job_status?: string
}

const APP_SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under \u20B120,000', '20k_40k': '\u20B120,000 \u2013 \u20B140,000',
  '40k_60k': '\u20B140,000 \u2013 \u20B160,000', '60k_80k': '\u20B160,000 \u2013 \u20B180,000',
  '80k_100k': '\u20B180,000 \u2013 \u20B1100,000', over_100k: 'Over \u20B1100,000',
}
const AVAIL_LABELS: Record<string, string> = {
  immediately: 'Immediately available', two_weeks: '2 weeks notice',
  one_month: '1 month notice', three_months: '3+ months',
}

const statusConfig: Record<ApplicationStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending:     { label: 'Applied',      color: 'text-yellow-700',   bg: 'bg-yellow-100',  icon: <Clock       className="w-3.5 h-3.5" /> },
  reviewed:    { label: 'Under Review', color: 'text-blue-700',     bg: 'bg-blue-100',    icon: <Eye         className="w-3.5 h-3.5" /> },
  shortlisted: { label: 'Interview',    color: 'text-green-700',    bg: 'bg-green-100',   icon: <Star        className="w-3.5 h-3.5" /> },
  rejected:    { label: 'Rejected',     color: 'text-red-700',      bg: 'bg-red-100',     icon: <XCircle     className="w-3.5 h-3.5" /> },
  hired:       { label: 'Hired',        color: 'text-emerald-700',  bg: 'bg-emerald-100', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  cancelled:   { label: 'Withdrawn',    color: 'text-gray-500',     bg: 'bg-gray-100',    icon: <XCircle     className="w-3.5 h-3.5" /> },
}

const TYPE_LABELS: Record<string, string> = {
  full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract',
  internship: 'Internship', freelance: 'Freelance',
}

const LEVEL_LABELS: Record<string, string> = {
  entry: 'Entry Level', mid: 'Mid Level', senior: 'Senior Level', executive: 'Executive',
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/* ─────────────────── Invitation Apply Modal ─────────────────────── */

interface InvitationApplyModalProps {
  invitation: JobInvitation
  onClose: () => void
  onApplied: () => void
}

const InvitationApplyModal: React.FC<InvitationApplyModalProps> = ({ invitation, onClose, onApplied }) => {
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<{
    first_name?: string; last_name?: string; program?: string
    department?: string; year_graduated?: number; skills_acquired?: string[]; phone?: string
  } | null>(null)
  const [coverLetter, setCoverLetter] = useState('')
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [phone, setPhone] = useState<string | undefined>(undefined)
  const [portfolioUrl, setPortfolioUrl] = useState('')
  const [expectedSalary, setExpectedSalary] = useState('')
  const [availability, setAvailability] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    supabaseClient
      .from('alumni_profiles')
      .select('first_name, last_name, program, department, year_graduated, skills_acquired, phone')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setProfile(data)
          if (data.phone) setPhone(data.phone)
        }
      })
  }, [user?.id])

  const handleSubmit = async () => {
    if (!user?.id) return
    if (!resumeFile) {
      toast.error('Please upload your resume before submitting')
      return
    }
    setSubmitting(true)
    try {
      // Upload resume
      const ext = resumeFile.name.split('.').pop()
      const filePath = `${user.id}/${invitation.job_id}.${ext}`
      const { error: uploadErr } = await supabaseClient.storage
        .from('resumes')
        .upload(filePath, resumeFile, { upsert: true })
      if (uploadErr) throw new Error(`Resume upload failed: ${uploadErr.message}`)

      // Build application data
      const applicationData: Record<string, any> = {}
      if (phone) applicationData.phone = phone
      if (portfolioUrl.trim()) applicationData.portfolio_url = portfolioUrl.trim()
      if (expectedSalary) applicationData.expected_salary = expectedSalary
      if (availability) applicationData.availability = availability

      const rpcArgs: Record<string, any> = {
        p_job_id: invitation.job_id,
        p_cover_letter: coverLetter || null,
        p_resume_url: filePath,
      }
      if (Object.keys(applicationData).length > 0) {
        rpcArgs.p_application_data = applicationData
      }

      const { error: applyErr } = await supabaseClient.rpc('apply_for_job', rpcArgs as any)
      if (applyErr) {
        if (applyErr.message?.includes('already have an active application') || applyErr.message?.includes('duplicate') || applyErr.message?.includes('unique')) {
          toast.error('You have already applied to this job')
        } else if (applyErr.message?.includes('can re-apply') || applyErr.message?.includes('re-apply to this job after')) {
          toast.error(applyErr.message)
        } else {
          throw applyErr
        }
      }

      // Mark invitation as accepted
      await supabaseClient.rpc('respond_to_invitation', {
        p_invitation_id: invitation.id,
        p_response: 'accepted',
      })

      notifyNewApplicationEmail(invitation.job_id).catch(() => {})
      toast.success('Application submitted!')
      onApplied()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit application')
    } finally {
      setSubmitting(false)
    }
  }

  const fullName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    : ''

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Apply for Position</h2>
            <p className="text-sm text-gray-500 mt-0.5">{invitation.job_title} at {invitation.company_name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Invited by banner */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5 flex items-center gap-2.5">
            <Send className="w-4 h-4 text-indigo-500 shrink-0" />
            <p className="text-sm text-indigo-700">
              You were invited by <strong>{invitation.company_name}</strong> — responding with this application will accept the invitation.
            </p>
          </div>

          {/* Applying as */}
          {profile && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-2.5">Applying as</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {fullName.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{fullName || 'Your Name'}</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                    {profile.program && <span>{profile.program}</span>}
                    {profile.year_graduated && <span>Class of {profile.year_graduated}</span>}
                    {profile.department && <span className="text-gray-400">·</span>}
                    {profile.department && <span>{profile.department}</span>}
                  </div>
                </div>
              </div>
              {(profile.skills_acquired?.length || 0) > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {profile.skills_acquired!.slice(0, 6).map(s => (
                    <span key={s} className="bg-white border border-blue-200 text-blue-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                  ))}
                  {(profile.skills_acquired?.length || 0) > 6 && (
                    <span className="bg-white border border-blue-200 text-gray-500 text-[11px] px-2 py-0.5 rounded-md">+{profile.skills_acquired!.length - 6} more</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cover Letter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Cover Letter <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={4}
              value={coverLetter}
              onChange={e => setCoverLetter(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tell the employer why you're a great fit for this role..."
            />
          </div>

          {/* Resume */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Resume <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(PDF, max 5 MB)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/30 transition">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Upload className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {resumeFile ? resumeFile.name : 'Click to upload your resume'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">PDF format only</p>
              </div>
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f && f.size > 5 * 1024 * 1024) { toast.error('File must be under 5 MB'); return }
                  setResumeFile(f || null)
                }}
              />
            </label>
          </div>

          {/* Additional Details */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Additional Details <span className="text-gray-400 font-normal">(all optional)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 bg-white">
                  <PhoneInput
                    international
                    defaultCountry="PH"
                    value={phone}
                    onChange={setPhone}
                    className="phone-input-custom w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Portfolio / LinkedIn</label>
                <input
                  type="url"
                  value={portfolioUrl}
                  onChange={e => setPortfolioUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expected Salary</label>
                <select
                  value={expectedSalary}
                  onChange={e => setExpectedSalary(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="">Not specified</option>
                  <option value="under_20k">Under ₱20,000</option>
                  <option value="20k_40k">₱20,000 – ₱40,000</option>
                  <option value="40k_60k">₱40,000 – ₱60,000</option>
                  <option value="60k_80k">₱60,000 – ₱80,000</option>
                  <option value="80k_100k">₱80,000 – ₱100,000</option>
                  <option value="over_100k">Over ₱100,000</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Availability</label>
                <select
                  value={availability}
                  onChange={e => setAvailability(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="">Not specified</option>
                  <option value="immediately">Immediately available</option>
                  <option value="two_weeks">2 weeks notice</option>
                  <option value="one_month">1 month notice</option>
                  <option value="three_months">3+ months</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2 transition"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

const AlumniMyApplicationsPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [applications, setApplications] = useState<MyApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | ApplicationStatus>('all')
  const [search, setSearch] = useState('')
  const [viewModalApp, setViewModalApp] = useState<MyApplication | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'applications' | 'invitations'>(() =>
    searchParams.get('tab') === 'invitations' ? 'invitations' : 'applications'
  )

  // Sync tab when URL query param changes (e.g. sidebar link clicked while already on page)
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'invitations') setActiveTab('invitations')
    else if (tab === null || tab === 'applications') setActiveTab('applications')
  }, [searchParams])
  const [invitations, setInvitations] = useState<JobInvitation[]>([])
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [respondingId, setRespondingId] = useState<string | null>(null)
  const [invitationApplyTarget, setInvitationApplyTarget] = useState<JobInvitation | null>(null)
  const [declineConfirmTarget, setDeclineConfirmTarget] = useState<JobInvitation | null>(null)

  // Profile update nudge state
  const [dismissedNudges, setDismissedNudges] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('hired_nudge_dismissed')
      return new Set(stored ? JSON.parse(stored) : [])
    } catch { return new Set() }
  })
  const [profileNudgeApp, setProfileNudgeApp] = useState<MyApplication | null>(null)
  const [nudgeSalaryRange, setNudgeSalaryRange] = useState('')
  const [savingNudge, setSavingNudge] = useState(false)

  const dismissNudge = (appId: string) => {
    setDismissedNudges(prev => {
      const next = new Set(prev)
      next.add(appId)
      localStorage.setItem('hired_nudge_dismissed', JSON.stringify([...next]))
      return next
    })
  }

  // Career timeline nudge state
  const [timelineDoneNudges, setTimelineDoneNudges] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('timeline_nudge_done')
      return new Set(stored ? JSON.parse(stored) : [])
    } catch { return new Set() }
  })
  const [timelineNudgeApp, setTimelineNudgeApp] = useState<MyApplication | null>(null)
  const [timelineNudgeForm, setTimelineNudgeForm] = useState({
    start_date: '', location: '', description: '', salary_range: '', is_current: true,
  })
  const [savingTimelineNudge, setSavingTimelineNudge] = useState(false)

  const markTimelineDone = (appId: string) => {
    setTimelineDoneNudges(prev => {
      const next = new Set(prev)
      next.add(appId)
      localStorage.setItem('timeline_nudge_done', JSON.stringify([...next]))
      return next
    })
  }

  const openTimelineNudge = (app: MyApplication) => {
    setTimelineNudgeForm({
      start_date: new Date().toISOString().split('T')[0],
      location: app.job_location || '',
      description: '',
      salary_range: app.job_salary_range || '',
      is_current: true,
    })
    setTimelineNudgeApp(app)
  }

  const handleViewResume = async (resumePath: string) => {
    try {
      const { data, error } = await supabaseClient.storage
        .from('resumes')
        .createSignedUrl(resumePath, 300)
      if (error) throw error
      window.open(data.signedUrl, '_blank')
    } catch {
      toast.error('Failed to get resume link')
    }
  }

  const fetchData = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabaseClient
        .from('job_applications')
        .select(`
          id, status, applied_at, reviewed_at, cover_letter, resume_url, application_data,
          jobs(id, title, status, employment_type, location, description, requirements, skills_required, salary_range, deadline, experience_level, is_partner_listing, posted_by, companies(name))
        `)
        .eq('alumni_id', user.id)
        .order('applied_at', { ascending: false })

      if (error) throw error

      const rawApps = (data || []).map((a: any) => ({
        id: a.id,
        job_id: a.jobs?.id || '',
        status: a.status,
        applied_at: a.applied_at,
        reviewed_at: a.reviewed_at,
        cover_letter: a.cover_letter,
        resume_url: a.resume_url,
        application_data: a.application_data || {},
        job_title: a.jobs?.title || 'Unknown Position',
        job_employment_type: a.jobs?.employment_type,
        job_location: a.jobs?.location,
        company_name: a.jobs?.companies?.name || 'Unknown Company',
        _posted_by: a.jobs?.posted_by as string | undefined,
        job_description: a.jobs?.description,
        job_requirements: a.jobs?.requirements,
        job_skills_required: a.jobs?.skills_required,
        job_salary_range: a.jobs?.salary_range,
        job_deadline: a.jobs?.deadline,
        job_experience_level: a.jobs?.experience_level,
        job_is_partner_listing: a.jobs?.is_partner_listing,
        job_status: a.jobs?.status as string | undefined,
      }))

      // Fetch employer (poster) emails
      const posterIds = [...new Set(rawApps.map(a => a._posted_by).filter(Boolean))]
      let emailMap: Record<string, string> = {}
      if (posterIds.length > 0) {
        const { data: posters } = await supabaseClient
          .from('users')
          .select('id, email')
          .in('id', posterIds)
        emailMap = Object.fromEntries((posters || []).map((u: any) => [u.id, u.email]))
      }

      const mapped: MyApplication[] = rawApps.map(a => ({
        id: a.id,
        job_id: a.job_id,
        status: a.status,
        applied_at: a.applied_at,
        reviewed_at: a.reviewed_at,
        cover_letter: a.cover_letter,
        resume_url: a.resume_url,
        application_data: a.application_data,
        employer_email: a._posted_by ? emailMap[a._posted_by] : undefined,
        job_title: a.job_title,
        job_employment_type: a.job_employment_type,
        job_location: a.job_location,
        company_name: a.company_name,
        job_description: a.job_description,
        job_requirements: a.job_requirements,
        job_skills_required: a.job_skills_required,
        job_salary_range: a.job_salary_range,
        job_deadline: a.job_deadline,
        job_experience_level: a.job_experience_level,
        job_is_partner_listing: a.job_is_partner_listing,
        job_status: a.job_status,
      }))

      setApplications(mapped)
    } catch (err: any) {
      console.error('Failed to load applications:', err)
      toast.error('Failed to load your applications')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => { fetchData() }, [fetchData])

  // ── Invitations ──
  const fetchInvitations = useCallback(async () => {
    if (!user?.id) return
    setInvitationsLoading(true)
    try {
      const { data, error } = await supabaseClient.rpc('get_my_invitations')
      if (error) throw error
      setInvitations((data as JobInvitation[]) || [])
    } catch (err) {
      console.error('Failed to fetch invitations:', err)
    } finally {
      setInvitationsLoading(false)
    }
  }, [user?.id])

  useEffect(() => { fetchInvitations() }, [fetchInvitations])

  const handleDeclineInvitation = async (invitationId: string) => {
    setRespondingId(invitationId)
    try {
      const { data, error } = await supabaseClient.rpc('respond_to_invitation', {
        p_invitation_id: invitationId,
        p_response: 'declined',
      })
      if (error) throw error
      const result = data as { success: boolean; message: string }
      if (!result.success) { toast.error(result.message); return }
      toast.success('Invitation declined.')
      fetchInvitations()
    } catch (err: any) {
      toast.error(err.message || 'Failed to decline invitation')
    } finally {
      setRespondingId(null)
    }
  }

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  const filtered = applications.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        a.job_title.toLowerCase().includes(q) ||
        a.company_name.toLowerCase().includes(q) ||
        (a.job_location || '').toLowerCase().includes(q)
      )
    }
    return true
  })

  const counts = {
    all:         applications.length,
    pending:     applications.filter(a => a.status === 'pending').length,
    reviewed:    applications.filter(a => a.status === 'reviewed').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    rejected:    applications.filter(a => a.status === 'rejected').length,
    hired:       applications.filter(a => a.status === 'hired').length,
    cancelled:   applications.filter(a => a.status === 'cancelled').length,
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {activeTab === 'invitations' ? 'Job Invitations' : 'My Applications'}
          </h1>
          <p className="text-blue-200 text-sm mt-1">
            {activeTab === 'invitations'
              ? 'Invitations from employers to apply for their job postings'
              : 'Track the status of all your job applications'}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {activeTab === 'invitations' ? (
              <>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5 text-yellow-300" />
                  {invitations.length} invitation{invitations.length !== 1 ? 's' : ''}
                </span>
                {pendingInvitations.length > 0 && (
                  <span className="bg-yellow-400/20 border border-yellow-300/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-yellow-300" />
                    {pendingInvitations.length} pending
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-yellow-300" />
                  {applications.length} application{applications.length !== 1 ? 's' : ''}
                </span>
                {counts.shortlisted > 0 && (
                  <span className="bg-green-500/20 border border-green-400/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 text-green-300" />
                    {counts.shortlisted} interview{counts.shortlisted !== 1 ? 's' : ''}
                  </span>
                )}
                {counts.hired > 0 && (
                  <span className="bg-yellow-400/20 border border-yellow-300/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-yellow-300" />
                    {counts.hired} hired
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs: Browse Jobs / My Applications / Invitations ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => navigate('/jobs')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 transition"
        >
          <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> Browse Jobs</span>
        </button>
        <button
          onClick={() => navigate('/my-applications?tab=applications')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'applications' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> My Applications</span>
        </button>
        <button
          onClick={() => navigate('/my-applications?tab=invitations')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'invitations' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2">
            <Send className="w-4 h-4" /> Invitations
            {pendingInvitations.length > 0 && (
              <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingInvitations.length}</span>
            )}
          </span>
        </button>
      </div>

      {/* ── Search ── */}
      {activeTab === 'applications' && (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by job title, company, or location..."
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      )}

      {/* ── Invitations Tab ── */}
      {activeTab === 'invitations' && (
        <div className="space-y-4">
          {invitationsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No invitations yet</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">When employers find your profile through candidate matching, their invitations will appear here.</p>
            </div>
          ) : (
            <>
              {/* Pending count banner */}
              {pendingInvitations.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center gap-2.5">
                  <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{pendingInvitations.length}</span>
                  </div>
                  <p className="text-sm text-indigo-700 font-medium">
                    You have {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''} — review and accept to submit your application.
                  </p>
                </div>
              )}

              {invitations.map(inv => {
                const isPending = inv.status === 'pending'
                const isAccepted = inv.status === 'accepted'
                const isDeclined = inv.status === 'declined'
                const isAdminExpired = inv.status === 'expired' && inv.is_admin_closed === true
                return (
                  <div
                    key={inv.id}
                    className={`bg-white rounded-2xl border shadow-sm transition overflow-hidden ${
                      isPending ? 'border-indigo-200' : isAdminExpired ? 'border-red-200 opacity-90' : 'border-gray-200 opacity-80'
                    }`}
                  >
                    {/* Admin-closed strip */}
                    {isAdminExpired && (
                      <div className="flex items-center gap-2 px-5 py-2 bg-red-50 border-b border-red-200">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <p className="text-xs text-red-700 font-medium">This job listing was closed by UCLM administration — the invitation is no longer valid.</p>
                      </div>
                    )}
                    <div className="p-5">
                      {/* Top: company + status badge */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {inv.company_logo ? (
                            <img src={inv.company_logo} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0 border border-gray-100" />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-indigo-900 text-white flex items-center justify-center text-base font-bold flex-shrink-0">
                              {(inv.company_name ?? 'C').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 leading-snug">{inv.job_title}</h3>
                            <p className="text-sm text-gray-500 mt-0.5">{inv.company_name}</p>
                          </div>
                        </div>
                        <span className={`flex-shrink-0 px-2.5 py-1 text-[11px] font-bold rounded-full ${
                          isPending      ? 'bg-indigo-100 text-indigo-700' :
                          isAccepted     ? 'bg-green-100 text-green-700' :
                          isDeclined     ? 'bg-gray-100 text-gray-500' :
                          isAdminExpired ? 'bg-red-100 text-red-600' :
                                           'bg-amber-100 text-amber-700'
                        }`}>
                          {isPending ? 'Pending' : isAccepted ? 'Applied' : isDeclined ? 'Declined' : isAdminExpired ? 'Closed by Admin' : 'Expired'}
                        </span>
                      </div>

                      {/* Job meta row */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-3">
                        {inv.employment_type && (
                          <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            <Briefcase className="w-3 h-3" />
                            {TYPE_LABELS[inv.employment_type] || inv.employment_type}
                          </span>
                        )}
                        {inv.experience_level && (
                          <span className="flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            <GraduationCap className="w-3 h-3" />
                            {LEVEL_LABELS[inv.experience_level] || inv.experience_level}
                          </span>
                        )}
                        {inv.job_location && (
                          <span className="flex items-center gap-1 text-gray-500">
                            <MapPin className="w-3 h-3" /> {inv.job_location}
                          </span>
                        )}
                        {inv.job_salary_range && (
                          <span className="flex items-center gap-1 text-gray-500">
                            <DollarSign className="w-3 h-3" /> {APP_SALARY_LABELS[inv.job_salary_range] || inv.job_salary_range}
                          </span>
                        )}
                        {inv.job_deadline && (
                          <span className={`flex items-center gap-1 ${new Date(inv.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-600'}`}>
                            <Calendar className="w-3 h-3" /> Deadline: {formatDate(inv.job_deadline)}
                          </span>
                        )}
                      </div>

                      {/* Skill tags */}
                      {(inv.job_skills ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {(inv.job_skills ?? []).slice(0, 6).map(s => (
                            <span key={s} className="bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                          ))}
                          {(inv.job_skills ?? []).length > 6 && (
                            <span className="bg-gray-100 text-gray-500 text-[11px] px-2 py-0.5 rounded-md">+{(inv.job_skills ?? []).length - 6} more</span>
                          )}
                        </div>
                      )}

                      {/* Employer personal message */}
                      {inv.message && (
                        <div className="bg-indigo-50 border-l-4 border-indigo-300 rounded-r-lg px-4 py-2.5 mb-3">
                          <p className="text-[11px] font-semibold text-indigo-500 uppercase tracking-wide mb-1">Message from {inv.company_name}</p>
                          <p className="text-sm text-indigo-800 italic">"{inv.message}"</p>
                        </div>
                      )}

                      {/* Footer meta */}
                      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-400">
                          Invited {formatDate(inv.invited_at)}
                          {inv.responded_at && ` · Responded ${formatDate(inv.responded_at)}`}
                        </p>

                        {isPending && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setDeclineConfirmTarget(inv)}
                              disabled={respondingId === inv.id}
                              className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
                            >
                              <XCircle className="w-4 h-4" />
                              {respondingId === inv.id ? '...' : 'Decline'}
                            </button>
                            <button
                              onClick={() => setInvitationApplyTarget(inv)}
                              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition"
                            >
                              <CheckCircle className="w-4 h-4" /> Accept & Apply
                            </button>
                          </div>
                        )}

                        {isAccepted && (
                          <button
                            onClick={() => setActiveTab('applications')}
                            className="flex items-center gap-1.5 text-sm text-green-700 font-medium hover:text-green-800 transition"
                          >
                            <Eye className="w-4 h-4" /> View Application
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}

      {activeTab === 'applications' && (
      <>
      {/* ── Status filter ── */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'pending', 'reviewed', 'shortlisted', 'hired', 'rejected', 'cancelled'] as const).map(s => {
          const count = counts[s]
          const isActive = statusFilter === s
          const badgeColors: Record<string, string> = {
            all:        isActive ? 'bg-blue-900 text-white'    : 'bg-gray-200 text-gray-600',
            pending:    isActive ? 'bg-yellow-500 text-white'  : 'bg-yellow-100 text-yellow-700',
            reviewed:   isActive ? 'bg-blue-500 text-white'    : 'bg-blue-100 text-blue-700',
            shortlisted:isActive ? 'bg-green-600 text-white'   : 'bg-green-100 text-green-700',
            hired:      isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700',
            rejected:   isActive ? 'bg-red-500 text-white'     : 'bg-red-100 text-red-600',
            cancelled:  isActive ? 'bg-gray-600 text-white'    : 'bg-gray-100 text-gray-500',
          }
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
                isActive
                  ? 'bg-white border-gray-900 text-gray-900 shadow-sm'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
              }`}
            >
              {s === 'all' ? 'All' : statusConfig[s].label}
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center ${badgeColors[s]}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700">
            {applications.length === 0 ? "No applications yet" : "No matching applications"}
          </h3>
          <p className="text-gray-500 mt-1 text-sm">
            {applications.length === 0
              ? "Start browsing jobs and apply to positions that interest you."
              : "Try adjusting your filters."}
          </p>
          {applications.length === 0 && (
            <button
              onClick={() => navigate('/jobs')}
              className="mt-4 px-5 py-2.5 bg-blue-900 text-white text-sm font-semibold rounded-lg hover:bg-blue-800 transition"
            >
              Browse Jobs
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(app => {
            const isAdminCancelled = app.status === 'cancelled' && app.job_status === 'closed'
            const sc = isAdminCancelled
              ? { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-100', icon: statusConfig.cancelled.icon }
              : statusConfig[app.status]
            const letter = app.company_name.charAt(0).toUpperCase()
            const showHiredNudge = app.status === 'hired' && !dismissedNudges.has(app.id)

            return (
              <React.Fragment key={app.id}>
                {(() => {
                  const isClosed = app.job_status === 'closed'
                  const borderColor = isClosed && app.status === 'cancelled'
                    ? 'border-red-300'
                    : isClosed
                    ? 'border-amber-300'
                    : showHiredNudge
                    ? 'border-emerald-300'
                    : 'border-gray-200'
                  return (
                    <div
                      className={`bg-white rounded-xl border ${borderColor} shadow-sm hover:shadow-md transition cursor-pointer overflow-hidden`}
                      onClick={() => setViewModalApp(app)}
                    >
                      {/* Hired nudge strip */}
                      {showHiredNudge && (
                        <div
                          className="flex items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50 border-b border-emerald-200"
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            <p className="text-xs text-emerald-700 font-medium truncate">
                              You were hired here — update your employment profile.
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {!timelineDoneNudges.has(app.id) && (
                              <button
                                onClick={() => openTimelineNudge(app)}
                                className="px-2.5 py-1 bg-white border border-emerald-400 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-50 transition"
                              >
                                Add to Timeline
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setNudgeSalaryRange(app.job_salary_range ? toDisplayValue(app.job_salary_range, 'salary_range') : '')
                                setProfileNudgeApp(app)
                              }}
                              className="px-2.5 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition"
                            >
                              Update Profile
                            </button>
                            <button
                              onClick={() => dismissNudge(app.id)}
                              className="p-1 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg transition"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                      {isClosed && app.status === 'cancelled' && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-200">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <p className="text-xs text-red-700 font-medium">Closed by UCLM administration — your application has been cancelled.</p>
                        </div>
                      )}
                      {isClosed && app.status === 'pending' && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <p className="text-xs text-amber-700 font-medium">Closed by UCLM administration — your application is under review.</p>
                        </div>
                      )}
                      {isClosed && ['reviewed', 'shortlisted'].includes(app.status) && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-200">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <p className="text-xs text-red-700 font-medium">Closed by UCLM administration — your application has been cancelled.</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-blue-900 text-white flex items-center justify-center text-base font-bold flex-shrink-0">
                        {letter}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{app.job_title}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                          <span className="font-medium text-gray-700">{app.company_name}</span>
                          {app.job_employment_type && <span>{TYPE_LABELS[app.job_employment_type]}</span>}
                          {app.job_location && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" /> {app.job_location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Applied: {formatDate(app.applied_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                        {sc.icon} {sc.label}
                      </span>
                      <Eye className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                  )
                })()}
              </React.Fragment>
            )
          })}
        </div>
      )}
      </>
      )}

      {/* Application view modal */}
      {viewModalApp && (() => {
        const sc = statusConfig[viewModalApp.status]
        const letter = viewModalApp.company_name.charAt(0).toUpperCase()
        return createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewModalApp(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-900 text-white flex items-center justify-center text-base font-bold flex-shrink-0">
                    {letter}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{viewModalApp.job_title}</h3>
                    <p className="text-xs text-gray-500">{viewModalApp.company_name} · Applied {formatDate(viewModalApp.applied_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                    {sc.icon} {sc.label}
                  </span>
                  <button onClick={() => setViewModalApp(null)} className="p-1 rounded-lg hover:bg-gray-100 transition"><X className="w-5 h-5 text-gray-400" /></button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Job info */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  {viewModalApp.job_employment_type && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">{TYPE_LABELS[viewModalApp.job_employment_type]}</span>}
                  {viewModalApp.job_location && (
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" /> {viewModalApp.job_location}
                    </span>
                  )}
                  {viewModalApp.job_experience_level && (
                    <span className="text-gray-500">{viewModalApp.job_experience_level}</span>
                  )}
                  {viewModalApp.job_is_partner_listing && (
                    <span className="bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full text-xs font-medium">Partner Listing</span>
                  )}
                </div>

                {/* Job Details */}
                {(viewModalApp.job_description || viewModalApp.job_requirements?.length || viewModalApp.job_skills_required?.length || viewModalApp.job_salary_range || viewModalApp.job_deadline) && (
                  <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-800">Job Details</h4>

                    {viewModalApp.job_salary_range && (
                      <div className="text-sm">
                        <span className="text-gray-500 font-medium">Salary Range: </span>
                        <span className="text-gray-800 font-semibold">{viewModalApp.job_salary_range}</span>
                      </div>
                    )}
                    {viewModalApp.job_deadline && (
                      <div className="text-sm flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-500 font-medium">Application Deadline:</span>
                        <span className="text-gray-800">{formatDate(viewModalApp.job_deadline)}</span>
                      </div>
                    )}

                    {viewModalApp.job_description && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Description</p>
                        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{viewModalApp.job_description}</p>
                      </div>
                    )}

                    {viewModalApp.job_requirements && viewModalApp.job_requirements.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {viewModalApp.job_requirements.map((req, i) => (
                            <li key={i} className="text-sm text-gray-700">{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {viewModalApp.job_skills_required && viewModalApp.job_skills_required.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                        <div className="flex flex-wrap gap-1.5">
                          {viewModalApp.job_skills_required.map((skill, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Status tracker */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Application Status</h4>
                  {viewModalApp.status === 'cancelled' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-500">
                      <XCircle className="w-4 h-4" /> Application Withdrawn
                    </span>
                  ) : (
                    <div className="flex items-center gap-1">
                      {(['pending', 'reviewed', 'shortlisted', 'hired'] as ApplicationStatus[]).map((s, i) => {
                        const past = ['pending', 'reviewed', 'shortlisted', 'hired'].indexOf(viewModalApp.status) >= i
                        const isCurrent = viewModalApp.status === s
                        return (
                          <React.Fragment key={s}>
                            <div className={`flex flex-col items-center ${isCurrent ? 'scale-105' : ''}`}>
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition ${
                                viewModalApp.status === 'rejected' && i > 0 ? 'bg-gray-100 text-gray-400' :
                                past ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-400'
                              }`}>
                                {i + 1}
                              </div>
                              <span className={`text-[10px] mt-1 font-medium ${past && viewModalApp.status !== 'rejected' ? 'text-blue-900' : 'text-gray-400'}`}>
                                {statusConfig[s].label}
                              </span>
                            </div>
                            {i < 3 && (
                              <div className={`flex-1 h-0.5 mb-4 ${
                                viewModalApp.status === 'rejected' ? 'bg-gray-200' :
                                ['pending', 'reviewed', 'shortlisted', 'hired'].indexOf(viewModalApp.status) > i ? 'bg-blue-900' : 'bg-gray-200'
                              }`} />
                            )}
                          </React.Fragment>
                        )
                      })}
                      {viewModalApp.status === 'rejected' && (
                        <span className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <XCircle className="w-3 h-3" /> Rejected
                        </span>
                      )}
                    </div>
                  )}
                  {viewModalApp.status === 'rejected' && viewModalApp.reviewed_at && (() => {
                    const reapplyTime = new Date(viewModalApp.reviewed_at).getTime() + 14 * 24 * 60 * 60 * 1000
                    if (reapplyTime > Date.now()) {
                      const reapplyDate = new Date(reapplyTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      return (
                        <div className="mt-2 flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                          <Clock className="w-4 h-4 shrink-0" />
                          <span>You can re-apply on <strong>{reapplyDate}</strong></span>
                        </div>
                      )
                    }
                    return null
                  })()}
                </div>

                {/* Application details */}
                {viewModalApp.application_data && Object.keys(viewModalApp.application_data).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Your Application Details</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm bg-gray-50 rounded-lg p-3">
                      {viewModalApp.application_data.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500">Phone:</span>
                          <span className="font-medium text-gray-800">{viewModalApp.application_data.phone}</span>
                        </div>
                      )}
                      {viewModalApp.application_data.portfolio_url && (
                        <div className="flex items-center gap-2">
                          <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500">Portfolio:</span>
                          <a href={viewModalApp.application_data.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                            View Portfolio
                          </a>
                        </div>
                      )}
                      {viewModalApp.application_data.expected_salary && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Expected Salary:</span>
                          <span className="font-medium text-gray-800">{APP_SALARY_LABELS[viewModalApp.application_data.expected_salary] || viewModalApp.application_data.expected_salary}</span>
                        </div>
                      )}
                      {viewModalApp.application_data.availability && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500">Availability:</span>
                          <span className="font-medium text-gray-800">{AVAIL_LABELS[viewModalApp.application_data.availability] || viewModalApp.application_data.availability}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cover letter & Resume */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {viewModalApp.cover_letter && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Your Cover Letter</h4>
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap max-h-36 overflow-y-auto">
                        {viewModalApp.cover_letter}
                      </div>
                    </div>
                  )}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Resume</h4>
                    {viewModalApp.resume_url ? (
                      <button
                        onClick={() => handleViewResume(viewModalApp.resume_url!)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                      >
                        <Download className="w-3.5 h-3.5" /> View Resume
                      </button>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No resume attached</p>
                    )}
                  </div>
                </div>

                {/* Employer contact */}
                {viewModalApp.employer_email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500">Employer:</span>
                    <span className="font-medium text-gray-800 select-all">{viewModalApp.employer_email}</span>
                  </div>
                )}
              </div>

              {/* Footer: withdraw action */}
              {(viewModalApp.status === 'pending' || viewModalApp.status === 'reviewed') && (
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                  {cancellingId === viewModalApp.id ? (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <p className="text-sm text-red-700 flex-1">Withdraw this application? This cannot be undone.</p>
                      <button
                        onClick={async () => {
                          const { error } = await supabaseClient.rpc('cancel_application', { p_application_id: viewModalApp.id })
                          if (error) { toast.error(error.message || 'Failed to withdraw'); return }
                          toast.success('Application withdrawn')
                          setCancellingId(null)
                          setViewModalApp(null)
                          fetchData()
                        }}
                        className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition shrink-0"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setCancellingId(null)}
                        className="px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-100 transition shrink-0"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setCancellingId(viewModalApp.id)}
                      className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium transition"
                    >
                      <XCircle className="w-4 h-4" /> Withdraw Application
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        , document.body)
      })()}
      {/* Profile Update Nudge Modal */}
      {profileNudgeApp && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setProfileNudgeApp(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Update Employment Profile</h3>
              <button onClick={() => setProfileNudgeApp(null)} className="p-1 hover:bg-gray-100 rounded-lg transition">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <p className="text-sm text-emerald-700">
                  Hired at <strong>{profileNudgeApp.company_name}</strong> as <strong>{profileNudgeApp.job_title}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 font-medium">Employed</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                <select
                  value={nudgeSalaryRange}
                  onChange={e => setNudgeSalaryRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Prefer not to disclose</option>
                  {SALARY_RANGE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-gray-400">
                You can update additional employment details on your{' '}
                <button className="underline hover:text-gray-600 transition" onClick={() => { navigate('/alumni/profile'); setProfileNudgeApp(null) }}>
                  profile page
                </button>.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setProfileNudgeApp(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!user?.id || !profileNudgeApp) return
                  setSavingNudge(true)
                  try {
                    const { error } = await supabaseClient
                      .from('alumni_profiles')
                      .update({
                        current_employment_status: 'employed',
                        job_position: profileNudgeApp.job_title,
                        salary_range: nudgeSalaryRange ? toDbValue(nudgeSalaryRange, 'salary_range') : null,
                        employment_length: null,
                      })
                      .eq('user_id', user.id)
                    if (error) throw error
                    toast.success('Employment profile updated!')
                    dismissNudge(profileNudgeApp.id)
                    setProfileNudgeApp(null)
                  } catch (err: any) {
                    toast.error(err.message || 'Failed to update profile')
                  } finally {
                    setSavingNudge(false)
                  }
                }}
                disabled={savingNudge}
                className="flex-1 px-4 py-2.5 bg-blue-900 text-white rounded-xl text-sm font-bold hover:bg-blue-800 transition disabled:opacity-50"
              >
                {savingNudge ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Career Timeline Nudge Modal */}
      {timelineNudgeApp && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setTimelineNudgeApp(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Add to Career Timeline</h3>
              <button onClick={() => setTimelineNudgeApp(null)} className="p-1 hover:bg-gray-100 rounded-lg transition">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <p className="text-sm text-emerald-700">
                  <strong>{timelineNudgeApp.job_title}</strong> at <strong>{timelineNudgeApp.company_name}</strong>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={timelineNudgeForm.start_date}
                    onChange={e => setTimelineNudgeForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={timelineNudgeForm.location}
                    onChange={e => setTimelineNudgeForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="e.g., Manila"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                <select
                  value={timelineNudgeForm.salary_range}
                  onChange={e => setTimelineNudgeForm(f => ({ ...f, salary_range: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Not specified</option>
                  <option value="under_20k">Under ₱20,000</option>
                  <option value="20k_40k">₱20,000 – ₱40,000</option>
                  <option value="40k_60k">₱40,000 – ₱60,000</option>
                  <option value="60k_80k">₱60,000 – ₱80,000</option>
                  <option value="80k_100k">₱80,000 – ₱100,000</option>
                  <option value="over_100k">Over ₱100,000</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={timelineNudgeForm.description}
                  onChange={e => setTimelineNudgeForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  placeholder="Brief summary of your role..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={timelineNudgeForm.is_current}
                  onChange={e => setTimelineNudgeForm(f => ({ ...f, is_current: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">I am currently working here</span>
              </label>

              <p className="text-xs text-gray-400">You can edit this entry anytime from your profile page.</p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setTimelineNudgeApp(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!user?.id || !timelineNudgeApp) return
                  if (!timelineNudgeForm.start_date) { toast.error('Please set a start date'); return }
                  setSavingTimelineNudge(true)
                  try {
                    const entryType = timelineNudgeApp.job_employment_type === 'internship' ? 'internship' : 'job'
                    const { error } = await supabaseClient
                      .from('career_timeline_entries')
                      .insert({
                        alumni_id: user.id,
                        entry_type: entryType,
                        title: timelineNudgeApp.job_title,
                        organization: timelineNudgeApp.company_name,
                        location: timelineNudgeForm.location.trim() || null,
                        start_date: timelineNudgeForm.start_date,
                        end_date: null,
                        is_current: timelineNudgeForm.is_current,
                        description: timelineNudgeForm.description.trim() || null,
                        employment_type: timelineNudgeApp.job_employment_type || null,
                        salary_range: timelineNudgeForm.salary_range || null,
                      })
                    if (error) throw error
                    toast.success('Added to your career timeline!')
                    markTimelineDone(timelineNudgeApp.id)
                    setTimelineNudgeApp(null)
                  } catch (err: any) {
                    toast.error(err.message || 'Failed to add timeline entry')
                  } finally {
                    setSavingTimelineNudge(false)
                  }
                }}
                disabled={savingTimelineNudge}
                className="flex-1 px-4 py-2.5 bg-blue-900 text-white rounded-xl text-sm font-bold hover:bg-blue-800 transition disabled:opacity-50"
              >
                {savingTimelineNudge ? 'Saving...' : 'Add to Timeline'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* ── Invitation Apply Modal ── */}
      {invitationApplyTarget && (
        <InvitationApplyModal
          invitation={invitationApplyTarget}
          onClose={() => setInvitationApplyTarget(null)}
          onApplied={() => {
            setInvitationApplyTarget(null)
            fetchData()
            fetchInvitations()
            setActiveTab('applications')
          }}
        />
      )}

      {/* ── Decline Confirmation Modal ── */}
      {declineConfirmTarget && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-50 rounded-xl flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Decline Invitation?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to decline the invitation for{' '}
                  <span className="font-semibold text-gray-700">"{declineConfirmTarget.job_title}"</span>{' '}
                  at <span className="font-semibold text-gray-700">{declineConfirmTarget.company_name}</span>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeclineConfirmTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const id = declineConfirmTarget.id
                  setDeclineConfirmTarget(null)
                  handleDeclineInvitation(id)
                }}
                disabled={respondingId === declineConfirmTarget.id}
                className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
              >
                Yes, Decline
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default AlumniMyApplicationsPage
